import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import SectionLoader from '@/components/Loader/SectionLoader';
import TextEditor from '@/components/TextEditor';
import { ECommonStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  addPolicySchema,
  TAddPolicyFormValues,
} from '@/schema/addPolicySchema';
import {
  addPolicy,
  updatePolicy,
  getPolicyById,
} from '@/service/editorialService';
import { IAddEditPolicyPayload } from '@/types/payload';
import { zodResolver } from '@hookform/resolvers/zod';

const DEFAULT_VALUES: TAddPolicyFormValues = {
  name: '',
  description: '',
};

const AddEditPolicy = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatText } = useFormatText();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const policyName = location.state?.policyName || '';

  const isEditMode = Boolean(id);

  const form = useForm<TAddPolicyFormValues>({
    resolver: zodResolver(addPolicySchema),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    control,
    formState: { errors },
  } = form;

  const editorialPageTitle = isEditMode
    ? formatText('editorial.policy.form.edit-title')
    : formatText('editorial.policy.form.add-title');
  const title = formatText('editorial.title');
  const breadcrumbLabel = isEditMode
    ? policyName
      ? `Edit ${policyName}`
      : formatText('common.actions.edit')
    : formatText('editorial.policy.form.add-title');

  // Set page title and breadcrumb
  useEffect(() => {
    const editorialTitle = title;
    const pageTitle = editorialPageTitle;

    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([
      {
        label: editorialTitle,
        to: '/editorial-management?tab=policy',
      },
      {
        label: breadcrumbLabel,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorialPageTitle, breadcrumbLabel]);

  // Load data for edit mode
  useEffect(() => {
    const fetchPolicyData = async () => {
      if (isEditMode && id) {
        setFetchingData(true);
        try {
          const response = await getPolicyById(id);

          if (response?.status && response?.data) {
            const policyData = response.data;
            reset({
              name: policyData.policy_name || '',
              description: policyData.description || '',
            });
          }
        } catch (error) {
          console.error('Error fetching policy:', error);
        } finally {
          setFetchingData(false);
        }
      } else {
        reset(DEFAULT_VALUES);
      }
      clearErrors();
    };

    fetchPolicyData();
  }, [isEditMode, id, reset, clearErrors]);

  const onSubmit = async (data: TAddPolicyFormValues) => {
    setLoading(true);

    try {
      let response;

      if (isEditMode && id) {
        // Update existing policy
        const payload: IAddEditPolicyPayload = {
          policy_name: data?.name,
          description: data?.description,
          status: ECommonStatus.ACTIVE,
        };
        response = await updatePolicy(id, payload);
      } else {
        // Add new policy
        const payload: IAddEditPolicyPayload = {
          policy_name: data?.name,
          description: data?.description,
        };
        response = await addPolicy(payload);
      }

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        navigate('/editorial-management?tab=policy');
      } else {
        console.error('Error saving policy:', response?.message);
      }
    } catch (error) {
      console.error('Error saving policy:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {fetchingData ? (
        <SectionLoader className="h-[50vh] flex justify-center" />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-6 px-1"
        >
          <Input
            name="name"
            label={formatText('editorial.policy.form.name-label')}
            placeholder={formatText('editorial.policy.form.name-placeholder')}
            register={register}
            error={
              errors.name?.message
                ? formatText(errors.name?.message)
                : undefined
            }
            isRequired
            disabled={loading}
          />

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <TextEditor
                label={formatText('editorial.policy.form.description-label')}
                isRequired
                error={
                  errors.description?.message
                    ? formatText(errors.description?.message)
                    : undefined
                }
                value={field.value}
                onChange={field.onChange}
                placeholder={formatText(
                  'editorial.policy.form.description-placeholder'
                )}
                disabled={loading}
                isFullToolbar
                wrapperClassName="policy-editor"
              />
            )}
          />

          <div className="ms-auto">
            <Button
              type="submit"
              variant="primary"
              label={
                isEditMode
                  ? formatText('editorial.policy.form.update-policy')
                  : formatText('editorial.policy.add-btn')
              }
              loading={loading}
              disabled={fetchingData}
            />
          </div>
        </form>
      )}
    </div>
  );
};

export default AddEditPolicy;
